import { useState, useMemo } from 'react'
import {
  alpha,
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  useTheme,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { useIntl } from 'react-intl'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import SideModalWrapper from '../../../Assets/modals/SideModalWrapper'
import { messages } from '../../messages'
import { useGetCarriersQuery } from '../../../../app/redux-fetch/apiOrders'
import { formatLocalDate } from '../../../../app/utils'
import { Route, LoadMetadata } from '../plannerTypes'

type ConsignmentRow = {
  loadId: number
  consignment: string
  type: string
  quantity: number
  tempControlled: string
  preferredCarrier: string
  schedArrival: string
  schedDeparture: string
}

type RouteRow = {
  routeIndex: number
  routeId: number
  carrier: string
  scheduledDeparture: string
  scheduledArrival: string
  transitIdentifier: string
  consignments: ConsignmentRow[]
}

type MergeRouteSidePanelProps = {
  open: boolean
  onClose: () => void
  onMerge: (
    selectedRouteIndices: number[],
    carrierId: number | undefined,
    carrierName: string | undefined,
    transitIdentifier: string,
    scheduledDeparture: string | undefined,
    scheduledArrival: string | undefined
  ) => void
  routes: Route[]
  loadMetadataById: Record<number, LoadMetadata>
  tenantId: number
  transportModeId?: number
}

const MergeRouteSidePanel = ({
  open,
  onClose,
  onMerge,
  routes,
  loadMetadataById,
  tenantId,
  transportModeId,
}: MergeRouteSidePanelProps) => {
  const theme = useTheme()
  const { formatMessage } = useIntl()
  const [selectedCarrierId, setSelectedCarrierId] = useState<number | ''>('')
  const [transitIdentifier, setTransitIdentifier] = useState('')
  const [scheduledDeparture, setScheduledDeparture] = useState<Date | null>(null)
  const [scheduledArrival, setScheduledArrival] = useState<Date | null>(null)
  const [selectedRouteIndices, setSelectedRouteIndices] = useState<number[]>([])
  const [expandedRoutes, setExpandedRoutes] = useState<number[]>([])

  const { data: carriers = [] } = useGetCarriersQuery(
    { tenantId },
    { skip: !open || !tenantId }
  )

  const carrierOptions = useMemo(() => {
    if (transportModeId) {
      return carriers.filter((carrier) => carrier.transportModeId === transportModeId)
    }
    return carriers
  }, [carriers, transportModeId])

  const routeRows: RouteRow[] = useMemo(() => {
    return routes.map((route, index) => {
      const loadIds = new Set<number>()
      route.destinations.forEach((dest) => {
        dest.loadActions.forEach((action) => {
          loadIds.add(action.loadId)
        })
      })

      const consignments: ConsignmentRow[] = Array.from(loadIds).map((loadId) => {
        const metadata = loadMetadataById[loadId]
        return {
          loadId,
          consignment: metadata?.trackingNumber || '-',
          type: metadata?.loadTypeName || '-',
          quantity: metadata?.quantity || 0,
          tempControlled: '-',
          preferredCarrier: '-',
          schedArrival: '-',
          schedDeparture: '-',
        }
      })

      return {
        routeIndex: index,
        routeId: route.id,
        carrier: route.carrierName || '-',
                scheduledDeparture: route.scheduledDepartureDate
                  ? formatLocalDate(route.scheduledDepartureDate)
                  : '-',
                scheduledArrival: route.scheduledArrivalDate
                  ? formatLocalDate(route.scheduledArrivalDate)
                  : '-',
        transitIdentifier: route.transitIdentifier || '-',
        consignments,
      }
    })
  }, [routes, loadMetadataById])

  const handleCarrierChange = (event: SelectChangeEvent<number | ''>) => {
    setSelectedCarrierId(event.target.value as number | '')
  }

  const handleTransitIdentifierChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTransitIdentifier(event.target.value)
  }

  const handleRouteCheckboxChange = (routeIndex: number) => {
    setSelectedRouteIndices((prev) =>
      prev.includes(routeIndex) ? prev.filter((idx) => idx !== routeIndex) : [...prev, routeIndex]
    )
  }

  const handleToggleExpand = (routeIndex: number) => {
    setExpandedRoutes((prev) =>
      prev.includes(routeIndex) ? prev.filter((idx) => idx !== routeIndex) : [...prev, routeIndex]
    )
  }

  const handleMergeClick = () => {
    if (selectedRouteIndices.length >= 2) {
      const selectedCarrier = carrierOptions.find((c) => c.id === selectedCarrierId)
      onMerge(
        selectedRouteIndices,
        selectedCarrierId || undefined,
        selectedCarrier?.name,
        transitIdentifier,
        scheduledDeparture?.toISOString(),
        scheduledArrival?.toISOString()
      )
      handleClose()
    }
  }

  const handleClose = () => {
    setSelectedCarrierId('')
    setTransitIdentifier('')
    setScheduledDeparture(null)
    setScheduledArrival(null)
    setSelectedRouteIndices([])
    setExpandedRoutes([])
    onClose()
  }

  const isMergeDisabled = selectedRouteIndices.length < 2

  return (
    <SideModalWrapper
      open={open}
      onClose={handleClose}
      headerLabel={formatMessage(messages.mergeRoute)}
      withBottomBox={false}
      width="800px"
    >
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="merge-carrier-label">
              {formatMessage(messages.carrierName)}
            </InputLabel>
            <Select
              labelId="merge-carrier-label"
              value={selectedCarrierId}
              label={formatMessage(messages.carrierName)}
              onChange={handleCarrierChange}
              data-testid="merge-carrier-select"
            >
              <MenuItem value="">
                <em>{formatMessage(messages.selectCarrier)}</em>
              </MenuItem>
              {carrierOptions.map((carrier) => (
                <MenuItem key={carrier.id} value={carrier.id}>
                  {carrier.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <DatePicker
            label={formatMessage(messages.scheduledDeparture)}
            value={scheduledDeparture}
            onChange={(newValue) => setScheduledDeparture(newValue)}
            format="YYYY-MM-DD"
            slotProps={{
              textField: {
                size: 'small',
                sx: { minWidth: 180 },
              },
            }}
          />

          <DatePicker
            label={formatMessage(messages.scheduledArrival)}
            value={scheduledArrival}
            onChange={(newValue) => setScheduledArrival(newValue)}
            format="YYYY-MM-DD"
            slotProps={{
              textField: {
                size: 'small',
                sx: { minWidth: 180 },
              },
            }}
          />
        </Box>

        <TextField
          fullWidth
          size="small"
          label={formatMessage(messages.transitIdentifier)}
          value={transitIdentifier}
          onChange={handleTransitIdentifierChange}
          sx={{ mb: 3 }}
          data-testid="merge-transit-identifier"
        />

        <Table size="small" data-testid="merge-routes-table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }} />
              <TableCell sx={{ color: '#636363', fontSize: '14px' }}>
                {formatMessage(messages.routes)}
              </TableCell>
              <TableCell sx={{ color: '#636363', fontSize: '14px' }}>
                {formatMessage(messages.carrierName)}
              </TableCell>
              <TableCell sx={{ color: '#636363', fontSize: '14px' }}>
                {formatMessage(messages.scheduledDeparture)}
              </TableCell>
              <TableCell sx={{ color: '#636363', fontSize: '14px' }}>
                {formatMessage(messages.scheduledArrival)}
              </TableCell>
              <TableCell sx={{ color: '#636363', fontSize: '14px' }}>
                {formatMessage(messages.transitIdentifier)}
              </TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              '& tr:nth-of-type(odd) > td': {
                backgroundColor: alpha(theme.palette.secondary.main, 0.08),
              },
            }}
          >
            {routeRows.map((row) => (
              <>
                <TableRow
                  key={row.routeId}
                  sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                  data-testid={`route-row-${row.routeIndex}`}
                >
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleToggleExpand(row.routeIndex)}
                      data-testid={`expand-route-${row.routeIndex}`}
                    >
                      {expandedRoutes.includes(row.routeIndex) ? (
                        <KeyboardArrowDownIcon />
                      ) : (
                        <KeyboardArrowRightIcon />
                      )}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    {formatMessage(messages.routeNumber, { number: row.routeIndex + 1 })}
                  </TableCell>
                  <TableCell>{row.carrier}</TableCell>
                  <TableCell>{row.scheduledDeparture}</TableCell>
                  <TableCell>{row.scheduledArrival}</TableCell>
                  <TableCell>{row.transitIdentifier}</TableCell>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedRouteIndices.includes(row.routeIndex)}
                      onChange={() => handleRouteCheckboxChange(row.routeIndex)}
                      data-testid={`route-checkbox-${row.routeIndex}`}
                    />
                  </TableCell>
                </TableRow>
                <TableRow key={`${row.routeId}-consignments`}>
                  <TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
                    <Collapse in={expandedRoutes.includes(row.routeIndex)} timeout="auto" unmountOnExit>
                      <Box sx={{ p: 2, ml: 4 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ color: '#636363', fontSize: '14px' }}>
                                {formatMessage(messages.consignment)}
                              </TableCell>
                              <TableCell sx={{ color: '#636363', fontSize: '14px' }}>
                                {formatMessage(messages.type)}
                              </TableCell>
                              <TableCell sx={{ color: '#636363', fontSize: '14px' }}>
                                {formatMessage(messages.quantity)}
                              </TableCell>
                              <TableCell sx={{ color: '#636363', fontSize: '14px' }}>
                                {formatMessage(messages.tempControlled)}
                              </TableCell>
                              <TableCell sx={{ color: '#636363', fontSize: '14px' }}>
                                {formatMessage(messages.preferredCarrier)}
                              </TableCell>
                              <TableCell sx={{ color: '#636363', fontSize: '14px' }}>
                                {formatMessage(messages.scheduledArrival)}
                              </TableCell>
                              <TableCell sx={{ color: '#636363', fontSize: '14px' }}>
                                {formatMessage(messages.scheduledDeparture)}
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody
                            sx={{
                              '& tr:nth-of-type(odd) > td': {
                                backgroundColor: alpha(theme.palette.secondary.main, 0.08),
                              },
                            }}
                          >
                            {row.consignments.map((consignment) => (
                              <TableRow key={consignment.loadId}>
                                <TableCell>{consignment.consignment}</TableCell>
                                <TableCell>{consignment.type}</TableCell>
                                <TableCell>{consignment.quantity}</TableCell>
                                <TableCell>{consignment.tempControlled}</TableCell>
                                <TableCell>{consignment.preferredCarrier}</TableCell>
                                <TableCell>{consignment.schedArrival}</TableCell>
                                <TableCell>{consignment.schedDeparture}</TableCell>
                              </TableRow>
                            ))}
                            {row.consignments.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={7} sx={{ textAlign: 'center' }}>
                                  {formatMessage(messages.noConsignments)}
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </>
            ))}
          </TableBody>
        </Table>

        {selectedRouteIndices.length > 0 && (
          <Box sx={{ mt: 2, color: 'text.secondary', fontSize: '0.875rem' }}>
            {formatMessage(messages.routesSelectedForMerge, { count: selectedRouteIndices.length })}
          </Box>
        )}

        {selectedRouteIndices.length === 1 && (
          <Box sx={{ mt: 1, color: 'warning.main', fontSize: '0.875rem' }}>
            {formatMessage(messages.selectRoutesToMerge)}
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
          <Button variant="outlined" onClick={handleClose} data-testid="merge-cancel-button">
            {formatMessage(messages.cancel)}
          </Button>
          <Button
            variant="contained"
            onClick={handleMergeClick}
            disabled={isMergeDisabled}
            data-testid="merge-confirm-button"
          >
            {formatMessage(messages.merge)}
          </Button>
        </Box>
      </Box>
    </SideModalWrapper>
  )
}

export default MergeRouteSidePanel
